import { useProfileShareLink } from "~vue/CollaborationProfile/composables/useProfileShareLink";
import { type ProfileShareSource, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import { i18n } from "~vue/i18n";
import type { ComputedRef, Ref } from "vue";

// These run outside component setup (click handlers, plain callers), so they read the global
// i18n instance at call time rather than useI18n().
const { t } = i18n.global;

export const profileShareMessage = (): string => t("profile.collaboration.shareMessages.share");
const profileShareEmailSubject = (): string => t("profile.collaboration.shareMessages.emailSubject");

export const profileShareBackMessage = (firstName: string): string =>
    firstName ? t("profile.collaboration.shareMessages.shareBack", { name: firstName }) : t("profile.collaboration.shareMessages.shareBackUnnamed");

export const profileInviteMessage = (firstName = ""): string =>
    firstName.trim() ? t("profile.collaboration.shareMessages.inviteWithName", { name: firstName.trim() }) : t("profile.collaboration.shareMessages.invite");

export type { ProfileShareTarget } from "~vue/CollaborationProfile/profileShareAttribution";

const SLACK_OPEN_DELAY_MS = 1000;

const teamsShareHref = (url: string, message: string = profileShareMessage()): string =>
    `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(message)}`;

export const openTeamsShare = (url: string, message: string = profileShareMessage()): void => {
    globalThis.open(teamsShareHref(url, message), "_blank", "noopener");
};

export const copyShareUrl = async (url: string, message: string = profileShareMessage()): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(`${message}\n\n${url}`);
        return true;
    } catch {
        return false;
    }
};

type ProfileShareActionsArgs = {
    profileId: ComputedRef<string | undefined>;
    // Accepts a writable ref (dialog's local access) or a computed (the card's derived access).
    access: Ref<string>;
    // Absolute URL to the user's own collaboration profile — used for organization-wide access
    // (no per-invite link) when sharing from a page other than the profile page itself.
    ownProfileUrl?: ComputedRef<string | undefined>;
    // An existing active per-invite share link, when the caller already has one.
    activeShareUrl?: ComputedRef<string | undefined>;
    shareSource: ProfileShareSource;
};

export const useProfileShareActions = ({ profileId, access, ownProfileUrl, activeShareUrl, shareSource }: ProfileShareActionsArgs) => {
    const { isGeneratingLink, shareLinkError, isCopied, ensureShareLink, handleCopyLink } = useProfileShareLink({
        profileId,
        activeShareUrl,
        ownProfileUrl,
        shareSource,
    });

    const copyLink = (message?: string): Promise<boolean> => handleCopyLink(access.value, undefined, message);

    // Copy the message + URL, then open Slack so the user can paste it.
    const shareViaSlack = (): Promise<boolean> =>
        handleCopyLink(access.value, () => setTimeout(() => globalThis.open("slack://", "_blank"), SLACK_OPEN_DELAY_MS), profileShareMessage(), "slack");

    const shareViaTeams = async (message: string = profileShareMessage()): Promise<boolean> => {
        shareLinkError.value = null;
        const url = await ensureShareLink(access.value);
        if (!url) {
            return false;
        }
        openTeamsShare(withShareAttribution(url, shareSource, "teams"), message);
        return true;
    };

    const shareViaEmail = async (recipientEmail?: string | null): Promise<boolean> => {
        shareLinkError.value = null;
        const url = await ensureShareLink(access.value);
        if (!url) {
            return false;
        }
        const body = `${profileShareMessage()}\n\n${withShareAttribution(url, shareSource, "email")}`;
        const to = recipientEmail ? encodeURIComponent(recipientEmail) : "";
        globalThis.location.href = `mailto:${to}?subject=${encodeURIComponent(profileShareEmailSubject())}&body=${encodeURIComponent(body)}`;
        return true;
    };

    return {
        isGeneratingLink,
        shareLinkError,
        isCopied,
        copyLink,
        shareViaSlack,
        shareViaTeams,
        shareViaEmail,
    };
};
